/**
 * Renewal Shared Typography Constants
 *
 * 이 파일은 shared 컴포넌트에서 사용할 타이포그래피 상수들을 정의합니다.
 * MUI 테마와 Figma 디자인 시스템을 기반으로 구성되었습니다.
 */

// Font Families
export const FONT_FAMILIES = {
  primary: '"Pretendard", "Roboto", "Helvetica", "Arial", sans-serif',
  secondary: '"Roboto Mono", "Monaco", "Consolas", monospace',
  display: '"Roboto Slab", "Georgia", serif',
} as const;

// Font Weights
export const FONT_WEIGHTS = {
  light: 300,
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
} as const;

// Font Sizes (px 단위)
export const FONT_SIZES = {
  xs: "10px", // Extra Small
  sm: "12px", // Small
  md: "14px", // Medium (Base)
  lg: "16px", // Large
  xl: "18px", // Extra Large
  xxl: "20px", // 2X Large
  xxxl: "24px", // 3X Large
  display: "32px", // Display
  hero: "60px", // Hero
  h1: "96px", // H1
  h2: "60px", // H2
  h3: "32px", // H3
  h4: "24px", // H4
  h5: "20px", // H5
  h6: "18px", // H6
} as const;

// Line Heights
export const LINE_HEIGHTS = {
  // Figma specific line heights (percentage)
  h1: "116.7%", // H1 line height
  h2: "120%", // H2 line height
  h3: "116.7%", // H3 line height
  h4: "123.5%", // H4 line height
  h5: "133.4%", // H5 line height
  h6: "160%", // H6 line height
  body1: "150%", // Body1 line height
  body2: "120%", // Body2 line height
  subtitle1: "175%", // Subtitle1 line height
  subtitle2: "157%", // Subtitle2 line height
  overline: "266%", // Overline line height
  caption1: "130%", // Caption1 line height
  caption2: "166%", // Caption2 line height
  // Figma specific line heights (pixel values)
  avatarInitialsLg: "20px", // Avatar initials large line height
  avatarInitialsSm: "10px", // Avatar initials small line height
  avatarInitialsMd: "12px", // Avatar initials medium line height
  badgeLabel: "20px", // Badge label line height
  buttonLarge: "26px", // Button large line height
  buttonMedium: "24px", // Button medium line height
  buttonSmall: "22px", // Button small line height
  chipLabel: "18px", // Chip label line height
  inputLabel: "12px", // Input label line height
  inputValue: "24px", // Input value line height
  listSubheader: "48px", // List subheader line height
  menuItemDefault: "24px", // Menu item default line height
  menuItemDense: "24px", // Menu item dense line height
  tableHeader: "24px", // Table header line height
  tooltipLabel: "14px", // Tooltip label line height
  dataGridAggregation: "12px", // DataGrid aggregation line height
} as const;

// Letter Spacing
export const LETTER_SPACING = {
  // Figma specific letter spacing (px 단위)
  h1: "-1.5px", // H1 letter spacing
  h2: "-0.5px", // H2 letter spacing
  h3: "0px", // H3 letter spacing
  h4: "0.25px", // H4 letter spacing
  h5: "0px", // H5 letter spacing
  h6: "0.15px", // H6 letter spacing
  body1: "0.15px", // Body1 letter spacing
  body2: "0.17px", // Body2 letter spacing
  subtitle1: "0.15px", // Subtitle1 letter spacing
  subtitle2: "0.1px", // Subtitle2 letter spacing
  overline: "1px", // Overline letter spacing
  caption1: "0px", // Caption1 letter spacing
  caption2: "0.4px", // Caption2 letter spacing
  buttonLarge: "0.46px", // Button large letter spacing
  buttonMedium: "0.4px", // Button medium letter spacing
  buttonSmall: "-0.25px", // Button small letter spacing
  chipLabel: "0.16px", // Chip label letter spacing
  inputLabel: "0.15px", // Input label letter spacing
  inputValue: "0.15px", // Input value letter spacing
  inputHelper: "0.4px", // Input helper letter spacing
  listSubheader: "0.1px", // List subheader letter spacing
  menuItemDefault: "0.15px", // Menu item default letter spacing
  menuItemDense: "0.17px", // Menu item dense letter spacing
  tableHeader: "0.17px", // Table header letter spacing
  tooltipLabel: "0px", // Tooltip label letter spacing
  dataGridAggregation: "0.15px", // DataGrid aggregation letter spacing
  avatarInitialsLg: "0.14px", // Avatar initials large letter spacing
  avatarInitialsSm: "0px", // Avatar initials small letter spacing
  avatarInitialsMd: "0px", // Avatar initials medium letter spacing
  badgeLabel: "0.14px", // Badge label letter spacing
  bottomNavigationActive: "0.4px", // Bottom navigation active letter spacing
  datePickerCurrentMonth: "0.15px", // Date picker current month letter spacing
  alertTitle: "0.15px", // Alert title letter spacing
  alertDescription: "0.15px", // Alert description letter spacing
} as const;

// Typography Scale (MUI 호환 + Figma 디자인)
export const TYPOGRAPHY_SCALE = {
  h1: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.light,
    fontSize: FONT_SIZES.h1,
    lineHeight: LINE_HEIGHTS.h1,
    letterSpacing: LETTER_SPACING.h1,
  },
  h2: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.light,
    fontSize: FONT_SIZES.h2,
    lineHeight: LINE_HEIGHTS.h2,
    letterSpacing: LETTER_SPACING.h2,
  },
  h3: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.h3,
    lineHeight: LINE_HEIGHTS.h3,
    letterSpacing: LETTER_SPACING.h3,
  },
  h4: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.h4,
    lineHeight: LINE_HEIGHTS.h4,
    letterSpacing: LETTER_SPACING.h4,
  },
  h5: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.bold,
    fontSize: FONT_SIZES.h5,
    lineHeight: LINE_HEIGHTS.h5,
    letterSpacing: LETTER_SPACING.h5,
  },
  h6: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.h6,
    lineHeight: LINE_HEIGHTS.h6,
    letterSpacing: LETTER_SPACING.h6,
  },
  body1: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.lg,
    lineHeight: LINE_HEIGHTS.body1,
    letterSpacing: LETTER_SPACING.body1,
  },
  body2: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.body2,
    letterSpacing: LETTER_SPACING.body2,
  },
  subtitle1: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.lg,
    lineHeight: LINE_HEIGHTS.subtitle1,
    letterSpacing: LETTER_SPACING.subtitle1,
  },
  subtitle2: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.subtitle2,
    letterSpacing: LETTER_SPACING.subtitle2,
  },
  overline: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.overline,
    letterSpacing: LETTER_SPACING.overline,
    textTransform: "uppercase" as const,
  },
  caption1: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.caption1,
    letterSpacing: LETTER_SPACING.caption1,
  },
  caption2: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.caption2,
    letterSpacing: LETTER_SPACING.caption2,
  },

  alertTitle: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.lg,
    lineHeight: LINE_HEIGHTS.body1,
    letterSpacing: LETTER_SPACING.alertTitle,
  },
  alertDescription: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.md,
    lineHeight: "143%",
    letterSpacing: LETTER_SPACING.alertDescription,
  },
  avatarInitialsLg: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.xxl,
    lineHeight: LINE_HEIGHTS.avatarInitialsLg,
    letterSpacing: LETTER_SPACING.avatarInitialsLg,
  },
  avatarInitialsSm: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.xs,
    lineHeight: LINE_HEIGHTS.avatarInitialsSm,
    letterSpacing: LETTER_SPACING.avatarInitialsSm,
  },
  avatarInitialsMd: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.avatarInitialsMd,
    letterSpacing: LETTER_SPACING.avatarInitialsMd,
  },
  badgeLabel: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.badgeLabel,
    letterSpacing: LETTER_SPACING.badgeLabel,
  },
  bottomNavigationActive: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.caption2,
    letterSpacing: LETTER_SPACING.bottomNavigationActive,
  },
  buttonLarge: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: "15px",
    lineHeight: LINE_HEIGHTS.buttonLarge,
    letterSpacing: LETTER_SPACING.buttonLarge,
  },
  buttonMedium: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.buttonMedium,
    letterSpacing: LETTER_SPACING.buttonMedium,
  },
  buttonSmall: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.buttonSmall,
    letterSpacing: LETTER_SPACING.buttonSmall,
  },
  chipLabel: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.chipLabel,
    letterSpacing: LETTER_SPACING.chipLabel,
  },
  datePickerCurrentMonth: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.lg,
    lineHeight: LINE_HEIGHTS.body1,
    letterSpacing: LETTER_SPACING.datePickerCurrentMonth,
  },
  inputLabel: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.inputLabel,
    letterSpacing: LETTER_SPACING.inputLabel,
  },
  inputValue: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.inputValue,
    letterSpacing: LETTER_SPACING.inputValue,
  },
  inputHelper: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.caption2,
    letterSpacing: LETTER_SPACING.inputHelper,
  },
  listSubheader: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.listSubheader,
    letterSpacing: LETTER_SPACING.listSubheader,
  },
  menuItemDefault: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.lg,
    lineHeight: LINE_HEIGHTS.menuItemDefault,
    letterSpacing: LETTER_SPACING.menuItemDefault,
  },
  menuItemDense: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.regular,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.menuItemDense,
    letterSpacing: LETTER_SPACING.menuItemDense,
  },
  tableHeader: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.md,
    lineHeight: LINE_HEIGHTS.tableHeader,
    letterSpacing: LETTER_SPACING.tableHeader,
  },
  tooltipLabel: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.xs,
    lineHeight: LINE_HEIGHTS.tooltipLabel,
    letterSpacing: LETTER_SPACING.tooltipLabel,
  },
  dataGridAggregation: {
    fontFamily: FONT_FAMILIES.primary,
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.sm,
    lineHeight: LINE_HEIGHTS.dataGridAggregation,
    letterSpacing: LETTER_SPACING.dataGridAggregation,
  },
} as const;

// Legacy Font Sizes (기존 시스템과의 호환성)
export const LEGACY_FONT_SIZES = {
  xLarge: "28px",
  xsLarge: "24px",
  xxsLarge: "20px",
  Large: "16px",
  medium: "14px",
  small: "12px",
} as const;

// 통합 Typography 객체
export const TYPOGRAPHY = {
  fontFamilies: FONT_FAMILIES,
  fontWeights: FONT_WEIGHTS,
  fontSizes: FONT_SIZES,
  lineHeights: LINE_HEIGHTS,
  letterSpacing: LETTER_SPACING,
  scale: TYPOGRAPHY_SCALE,
  legacy: LEGACY_FONT_SIZES,
} as const;

// 타입 정의
export type FontFamilyKey = keyof typeof FONT_FAMILIES;
export type FontWeightKey = keyof typeof FONT_WEIGHTS;
export type FontSizeKey = keyof typeof FONT_SIZES;
export type LineHeightKey = keyof typeof LINE_HEIGHTS;
export type LetterSpacingKey = keyof typeof LETTER_SPACING;
export type TypographyScaleKey = keyof typeof TYPOGRAPHY_SCALE;
export type LegacyFontSizeKey = keyof typeof LEGACY_FONT_SIZES;

// Figma 디자인 시스템 기반 추가 타입
export type FigmaTypographyKey =
  | "caption1"
  | "caption2"
  | "alertTitle"
  | "alertDescription"
  | "avatarInitialsLg"
  | "avatarInitialsSm"
  | "avatarInitialsMd"
  | "badgeLabel"
  | "bottomNavigationActive"
  | "buttonLarge"
  | "buttonMedium"
  | "buttonSmall"
  | "chipLabel"
  | "datePickerCurrentMonth"
  | "inputLabel"
  | "inputValue"
  | "inputHelper"
  | "listSubheader"
  | "menuItemDefault"
  | "menuItemDense"
  | "tableHeader"
  | "tooltipLabel"
  | "dataGridAggregation";
